import { createFileRoute } from "@tanstack/react-router";
import {
  Briefcase, Users, DollarSign, Target, Mail, Phone, CalendarDays, AtSign,
  Share2, MessageCircle, Funnel, Layout,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { ServiceCategoryGrid, ServiceHighlights } from "@/components/sections/service-category";

export const Route = createFileRoute("/marketing/lead-generation")({
  component: LeadGenPage,
  head: () => ({
    meta: [
      { title: "Lead Generation Services — B2B, B2C & Outbound | CoderLity" },
      { name: "description", content: "High-intent B2B and B2C leads, cold calling, appointment setting, LinkedIn, email, WhatsApp and SMS lead generation campaigns." },
      { property: "og:title", content: "Lead Generation Services — CoderLity" },
      { property: "og:description", content: "Fill your pipeline with qualified, ready-to-buy leads." },
      { property: "og:url", content: "/marketing/lead-generation" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/lead-generation" }],
  }),
});

function LeadGenPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Lead Generation"
        title="A pipeline full of ready-to-buy leads"
        sub="Multi-channel lead generation built for predictable, scalable revenue — without burning your brand."
      />
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <ServiceCategoryGrid
            groups={[
              {
                title: "Lead Types",
                items: [
                  { label: "B2B Leads", icon: Briefcase, desc: "Decision-maker leads from your ICP, verified and enriched.", href: "/marketing/lead-generation/b2b-leads" },
                  { label: "B2C Leads", icon: Users, desc: "High-volume consumer leads tuned to your offer.", href: "/marketing/lead-generation/b2c-leads" },
                  { label: "Sales Leads", icon: DollarSign, desc: "Bottom-of-funnel leads ready for a sales call.", href: "/marketing/lead-generation/sales-leads" },
                  { label: "Targeted Leads", icon: Target, desc: "Hyper-targeted by industry, geo, title and intent.", href: "/marketing/lead-generation/targeted-leads" },
                  { label: "Email Leads", icon: Mail, desc: "Permission-based email lists for nurturing.", href: "/marketing/lead-generation/email-leads" },
                ],
              },
              {
                title: "Outbound",
                items: [
                  { label: "Call Selling", icon: Phone, desc: "Trained closers selling your product over the phone.", href: "/marketing/lead-generation/call-selling" },
                  { label: "Cold Calling", icon: Phone, desc: "Compliant cold call campaigns with daily reporting.", href: "/marketing/lead-generation/cold-calling" },
                  { label: "Appointment Setting", icon: CalendarDays, desc: "Qualified meetings booked directly into your calendar.", href: "/marketing/lead-generation/appointment-setting" },
                  { label: "LinkedIn Leads", icon: AtSign, desc: "LinkedIn outreach + Sales Navigator prospecting.", href: "/marketing/lead-generation/linkedin-leads" },
                  { label: "Facebook Leads", icon: Share2, desc: "Lead form ads and Messenger qualification funnels.", href: "/marketing/lead-generation/facebook-leads" },
                ],
              },
              {
                title: "Channels & CRM",
                items: [
                  { label: "WhatsApp Marketing", icon: MessageCircle, desc: "Broadcast and 1:1 WhatsApp Business campaigns.", href: "/marketing/lead-generation/whatsapp-marketing" },
                  { label: "SMS Marketing", icon: Phone, desc: "Compliant SMS blasts and drip sequences.", href: "/marketing/lead-generation/sms-marketing" },
                  { label: "CRM Management", icon: Briefcase, desc: "HubSpot, Pipedrive and Zoho setup, hygiene and reporting.", href: "/marketing/lead-generation/crm-management" },
                  { label: "Funnel Marketing", icon: Funnel, desc: "End-to-end funnel design, build and optimization.", href: "/marketing/lead-generation/funnel-marketing" },
                  { label: "Landing Page Leads", icon: Layout, desc: "High-converting landing pages with A/B testing.", href: "/marketing/lead-generation/landing-page-leads" },
                ],
              },

            ]}
          />
        </div>
      </section>
      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <h2 className="text-xl font-bold tracking-tight">How we deliver</h2>
            <div className="mt-4">
              <ServiceHighlights items={[
                "ICP & messaging workshop",
                "Verified contact data (email + phone)",
                "Dedicated SDR team",
                "Weekly performance reviews",
                "CRM integration & handoff",
                "GDPR & TCPA compliant",
              ]} />
            </div>
          </div>
        </div>
      </section>
      <CTABanner />
    </PageShell>
  );
}
